import React, { useState, useEffect } from "react";
import axios from "axios";
import TemplateForm from "./TemplateForm";
import "./SmsTemplates.css";

const API_BASE = `${process.env.REACT_APP_API_URL}/templates`;

const SmsTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Fetch templates from backend
  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE}/list`);
      if (res.data.success) {
        // filter sirf SMS templates
        setTemplates(res.data.data.filter((t) => t.type === "sms"));
      }
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // ✅ Save template (add / update)
  const handleSave = async (templateData) => {
    try {
      const payload = {
        type: "sms",
        templateName: templateData.name,
        body: templateData.message,
        variables: [],
      };

      if (editingTemplate) {
        await axios.put(
          `${API_BASE}/update/${editingTemplate.id}`,
          payload
        );
        setEditingTemplate(null);
      } else {
        await axios.post(`${API_BASE}/add`, payload);
      }

      fetchTemplates();
    } catch (error) {
      console.error("Failed to save SMS template:", error);
    }
  };

  // ✅ Edit template
  const handleEdit = (template) => {
    setEditingTemplate({
      id: template._id,
      name: template.templateName || "",
      message: template.body || "",
    });
    document.querySelector(".template-form")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  // ✅ Delete template
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await axios.delete(`${API_BASE}/delete/${id}`);
        fetchTemplates();
      } catch (error) {
        console.error("Failed to delete SMS template:", error);
      }
    }
  };

  // ✅ Filter templates by search
  const filteredTemplates = templates.filter(
    (template) =>
      (template.templateName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (template.body || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return <div className="loading">Loading SMS templates...</div>;
  }

  return (
    <div className="sms-templates">
      <header className="templates-header">
        <h2>SMS Templates Manager</h2>
        <p>Create and manage your SMS message templates</p>
      </header>

      <TemplateForm
        onSave={handleSave}
        initialData={editingTemplate}
        onCancel={() => setEditingTemplate(null)}
        isEditing={!!editingTemplate}
      />

      <div className="saved-templates">
        <div className="templates-controls">
          <h3>Saved Templates ({templates.length})</h3>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>

        {filteredTemplates.length === 0 ? (
          <div className="empty-state">
            {searchTerm
              ? "No templates match your search"
              : "No SMS templates saved yet. Create your first one!"}
          </div>
        ) : (
          <div className="templates-grid">
            {filteredTemplates.map((template) => (
              <div
                key={template._id}
                className={`template-item ${
                  editingTemplate?.id === template._id ? "active" : ""
                }`}
              >
                <div className="template-content">
                  <h4>{template.templateName}</h4>
                  <p>{template.body}</p>
                  <div className="template-meta">
                    <span>
                      Created:{" "}
                      {template.createdAt
                        ? new Date(template.createdAt).toLocaleString()
                        : "N/A"}
                    </span>
                  </div>
                </div>
                <div className="template-actions">
                  <button
                    onClick={() => handleEdit(template)}
                    className="edit-btn"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(template._id)}
                    className="delete-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SmsTemplates;
